import { CheckCircleFilled } from "@ant-design/icons";
import { Button, message } from "antd";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BusinessState, getBusinessInfo } from "redux-context/business";
import { disconnectSocialMedia } from "services/SauceService";
import { handleTikTokLogin } from "helpers/tiktok-auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTiktok } from "@fortawesome/free-brands-svg-icons";
import "./ConnectSocial.scss";

const ConnectSocialTikTok: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [loading, setLoading] = useState<boolean>(false);

  const { business } = useSelector((state: BusinessState) => state.business);

  const tiktokAccounts = useMemo(() => {
    return (
      business?.socialMediasConnected?.filter(
        (socialMedia) =>
          socialMedia?.socialMediaName?.toLowerCase() === "tiktok"
      ) || []
    );
  }, [business]);

  const isConnected = tiktokAccounts.length > 0;

  const onClickConnectTikTok = async () => {
    setLoading(true);

    try {
      await handleTikTokLogin();
    } catch (error) {
      console.error(error);
      message.error("Unable to start TikTok authorization.");
    } finally {
      setLoading(false);
    }
  };

  const onClickDisconnectSocialMedia = async () => {
    try {
      setLoading(true);
      await disconnectSocialMedia({
        businessId: localStorage.getItem("businessId"),
        targetSocialMedia: "tiktok",
      });
      message.success("TikTok account disconnected successfully");
      dispatch(getBusinessInfo());
    } catch (error: any) {
      console.log(error);
      message.error("Error disconnecting TikTok account");
    } finally {
      setLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="connect-social-container connect-social-container--tiktok">
        <div className="icon-container">
          <FontAwesomeIcon
            icon={faTiktok}
            fontSize={24}
            className="platform-icon"
          />
        </div>
        <div className="text-container">
          <p className="title">TikTok</p>
          <p className="description">
            Connect your TikTok account to sync performance insights
          </p>
        </div>
        <div className="button-container-connection">
          <Button
            type="default"
            loading={loading}
            className="connect-button"
            onClick={onClickConnectTikTok}
          >
            Connect
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="connect-social-container connected connect-social-container--tiktok connected">
      <div className="connected-header">
        <div className="icon-container">
          <FontAwesomeIcon
            icon={faTiktok}
            fontSize={24}
            className="platform-icon"
          />
        </div>
        <div className="text-container">
          <p className="title">
            TikTok <CheckCircleFilled className="check-icon" />
          </p>
          <p className="description connected">Connected successfully</p>
        </div>
        <div className="button-disconnect-container">
          <Button
            type="link"
            className="disconnect-button"
            onClick={onClickDisconnectSocialMedia}
          >
            Disconnect
          </Button>
        </div>
      </div>

      {tiktokAccounts.length > 0 && (
        <div className="accounts-container">
          <p className="accounts-title">
            {tiktokAccounts.length}{" "}
            {tiktokAccounts.length === 1 ? "account" : "accounts"} connected:
          </p>
          <div className="accounts-list">
            {tiktokAccounts.map((account, index) => (
              <div key={account._id || index} className="account-badge">
                <FontAwesomeIcon
                  icon={faTiktok}
                  fontSize={24}
                  className="platform-icon small"
                />
                {account.accountName ? `@${account.accountName}` : "Account"}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectSocialTikTok;
